<template>
  <DxCardView
    :data-source="callsDataSource"
    :column-chooser="{ enabled: false }"
    :remote-operations="true"
    :header-panel="{ visible: false }"
    @content-ready="onContentReady"
    ref="cardViewRef"
    cards-per-row="1"
    :key-expr="id"
    :no-data-text="getNoDataText()"
    class="calls-card-view"
    card-content-template="callCardContentTemplate"
  >
    <DxCardHeader :visible="true" template="headerTemplate" />
    <template #headerTemplate="data">
      <div class="card-header-items">
        <span
          v-if="data.data.card.data.itemType === 2"
          v-html="
            schemeTranslated
              ? data.data.card.data.transName
              : data.data.card.data.name
          "
          class="title"
        ></span>
        <router-link
          v-else
          class="title"
          style="text-decoration: none"
          :to="{
            name: 'GrantDetails',
            params: { grantId: data.data.card.data.id },
          }"
          v-bind:target="openDetailsInNewTab ? '_blank' : null"
        >
          <span
            v-html="
              schemeTranslated
                ? data.data.card.data.transName
                : data.data.card.data.name
            "
          ></span>
        </router-link>
        <div class="card-header-items-right">
          <i
            class="flex"
            :id="`callTypeIcon-${data.data.card.data.id}`"
            :class="
              getClassForIcon(
                data.data.card.data.recurringYearsDuration,
                data.data.card.data.isAlwaysOpen,
              )
            "
          >
            <DxTooltip
              :hide-on-outside-click="false"
              :target="`#callTypeIcon-${data.data.card.data.id}`"
              show-event="mouseenter"
              hide-event="mouseleave"
              position="bottom"
              :container="'body'"
            >
              {{
                getToolTipData(
                  data.data.card.data.recurringYearsDuration,
                  data.data.card.data.isAlwaysOpen,
                )
              }}
            </DxTooltip></i
          >
          <span
            class="flex"
            v-if="data.data.card.data.recurringYearsDuration > 0"
          >
            {{ data.data.card.data.recurringYearsDuration }}
          </span>
          <DxButton
            class="badge-button"
            :text="t('scheme_details.related_opening')"
            :disabled="true"
          />
        </div>
      </div>
    </template>
    <template #callCardContentTemplate="{ data: { card } }">
      <div class="dates">
        <div class="date-item" v-if="!card.data.isAlwaysOpen">
          <span class="date-label"
            >{{ t("scheme_details.start_date") }}: &nbsp;</span
          >
          <span class="date-value">{{ formatDate(card.data.callStart) }}</span>
        </div>
        <div class="date-item deadline-date">
          <span class="date-label"
            >{{ t("scheme_details.deadline") }}: &nbsp;</span
          >
          <span v-if="!card.data.isAlwaysOpen" class="date-value">{{
            formatDate(card.data.callEnd)
          }}</span>
          <span v-else class="date-value">
            <i
              class="las la-infinity"
              style="font-size: 1.6em"
              id="alwaysOpenIcon"
            >
              <DxTooltip
                :hide-on-outside-click="false"
                target="#alwaysOpenIcon"
                show-event="mouseenter"
                hide-event="mouseleave"
                position="bottom"
                :container="'body'"
              >
                {{ t("scheme_details.always_open") }}
              </DxTooltip>
            </i>
          </span>
        </div>
      </div>

      <div
        class="body"
        v-html="schemeTranslated ? card.data.transBody : card.data.body"
      ></div>
      <div
        v-if="card.data.itemType === 2"
        class="application-period-additional-info"
      >
        <div
          v-if="
            isVind &&
            ((card.data.schemeLinks && card.data.schemeLinks.length > 0) ||
              (card.data.schemeDocuments &&
                card.data.schemeDocuments.length > 0))
          "
          class="lg-item-width"
        >
          <span class="box-title-small">
            {{ t("scheme_details.external_information") }}</span
          >
          <div
            class="scheme-links-sorted"
            v-if="
              getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              ) &&
              getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              ).length > 0
            "
          >
            <div
              v-for="link in getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              )"
              :key="link.id"
              class="links-list-sorted"
            >
              <div
                v-if="
                  link.url &&
                  link.url.length > 0 &&
                  link.documentType === EDocumentType.UsefulLinks
                "
                class="sorted-link row"
              >
                <i class="las la-2x la-link link-icon"></i>

                <span
                  class="doc-type"
                  v-if="link.documentType === EDocumentType.UsefulLinks"
                >
                  {{ t("scheme_details.useful_links") }}
                </span>
                <span class="doc-type" v-else>
                  {{ link.documentTypeDescription }}
                </span>
                <a class="link-item" :href="link.url" target="_blank">
                  {{ link.text }}
                </a>
              </div>
            </div>
          </div>
          <span class="box-title-small">
            {{ t("scheme_details.other_links_and_files") }}
          </span>
          <div
            class="scheme-links-sorted"
            v-if="
              getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              ) &&
              getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              ).length > 0
            "
          >
            <div
              v-for="link in getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              )"
              :key="link.id"
              class="links-list-sorted"
            >
              <div
                v-if="
                  link.url &&
                  link.url.length > 0 &&
                  link.documentType != EDocumentType.UsefulLinks &&
                  link.documentType != EDocumentType.VisualPingLink &&
                  !isDeprecatedDocumentType(link.documentType)
                "
                class="sorted-link row"
              >
                <i class="las la-2x la-link link-icon"></i>
                <span class="doc-type">
                  {{ link.documentTypeDescription }}
                </span>

                <a class="link-item" :href="link.url" target="_blank">
                  {{ link.text }}
                </a>
              </div>
              <div
                v-if="
                  !link.url &&
                  link.documentType != EDocumentType.UsefulLinks &&
                  link.documentType != EDocumentType.VisualPingLink &&
                  !isDeprecatedDocumentType(link.documentType)
                "
                class="sorted-doc row"
              >
                <i
                  :class="getIconBasedOnExtension(link.extension)"
                  class="link-icon"
                ></i>
                <span class="doc-type">
                  {{ link.documentTypeDescription }}
                </span>

                <div
                  class="link-item docname"
                  @click="download(link.id, link.name)"
                >
                  {{ link.name }}
                </div>
              </div>
            </div>
          </div>

          <DxButton
            v-if="!seeDeprecatedItems"
            class="deprecated-btn"
            @click="updateSeeDeprecatedItems(true)"
            styling-mode="text"
            :text="t('scheme_details.see_deprecated')"
            icon="las la-angle-down"
            iconPosition="right"
          />
          <DxButton
            v-else
            class="deprecated-btn"
            @click="updateSeeDeprecatedItems(false)"
            :text="t('scheme_details.hide_deprecated')"
            styling-mode="text"
            icon="las la-angle-up"
            iconPosition="right"
          />

          <div
            class="scheme-links-sorted"
            v-if="
              getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              ) &&
              getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              ).length > 0 &&
              seeDeprecatedItems
            "
          >
            <div
              v-for="link in getSortedLinksAndDocs(
                card.data.schemeLinks,
                card.data.schemeDocuments,
              )"
              :key="link.id"
              class="links-list-sorted"
            >
              <div
                v-if="
                  link.url &&
                  link.url.length > 0 &&
                  link.documentType != EDocumentType.UsefulLinks &&
                  link.documentType != EDocumentType.VisualPingLink &&
                  isDeprecatedDocumentType(link.documentType)
                "
                class="sorted-link row"
              >
                <i class="las la-2x la-link link-icon"></i>
                <span class="doc-type">
                  {{ link.documentTypeDescription }}
                </span>

                <a class="link-item" :href="link.url" target="_blank">
                  {{ link.text }}
                </a>
              </div>
              <div
                v-if="
                  !link.url &&
                  link.documentType != EDocumentType.UsefulLinks &&
                  link.documentType != EDocumentType.VisualPingLink &&
                  isDeprecatedDocumentType(link.documentType)
                "
                class="sorted-doc row"
              >
                <i
                  :class="getIconBasedOnExtension(link.extension)"
                  class="link-icon"
                ></i>
                <span class="doc-type">
                  {{ link.documentTypeDescription }}
                </span>

                <div
                  class="link-item docname"
                  @click="download(link.id, link.name)"
                >
                  {{ link.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <DxPaging :page-size="10" />
    <DxPager
      :visible="true"
      :display-mode="compact"
      :show-navigation-buttons="true"
      :show-info="true"
      :show-page-size-selector="true"
      :allowed-page-sizes="[10, 20, 50, 'all']"
      :info-text="pageInfoText"
    />
  </DxCardView>
</template>
<script>
import { ref, watch, computed, inject } from "vue";
import {
  DxCardView,
  DxPaging,
  DxCardHeader,
  DxPager,
} from "devextreme-vue/card-view";
import { DxTooltip } from "devextreme-vue/tooltip";
import { DxButton } from "devextreme-vue/button";
import { createCardViewDataSource } from "../store/modules/custom.store";
import { useI18n } from "vue-i18n";
import {
  EDocumentType,
  isDeprecatedDocumentType,
} from "../enums/EDocumentType";
import GrantsService from "../services/grants.service";
import store from "../store";
import moment from "moment";

export default {
  components: {
    DxCardView,
    DxPaging,
    DxPager,
    DxCardHeader,
    DxTooltip,
    DxButton,
  },
  props: {
    selectedTabIndex: Number,
    parentSchemeId: Number,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const isVind = inject("isVind");
    const seeDeprecatedItems = ref(false);
    const cardViewRef = ref(null);
    const selectedTab = ref(props.selectedTabIndex);
    const schemeId = ref(props.parentSchemeId);
    const schemeTranslated = computed(() => store.state.grantItems.translateScheme);
    const openDetailsInNewTab = computed({
      get: () => store.state.user.openDetailsInNewTab,
    });

    const updateSeeDeprecatedItems = (val) => {
      seeDeprecatedItems.value = val;
    };

    const callsDataSource = ref(
      createCardViewDataSource({
        parentSchemeId: schemeId.value,
        timeScope: selectedTab.value,
        translateScheme: schemeTranslated.value
      }),
    );

    const refreshCardView = () => {
      cardViewRef.value.instance.refresh();
    };

    watch(schemeTranslated.value, (next, prev) => {
      if (next === true && prev === false) refreshCardView();
    });
    watch(selectedTab.value, (next, prev) => {
      if (prev && next !== prev) refreshCardView();
    });

    const onContentReady = (e) => {
      const visibleRowsIds = cardViewRef.value.instance
        .getDataSource()
        .items()
        .map((i) => i.id);
      emit("items-loaded", visibleRowsIds);
    };

    const pageInfoText = computed(
      () =>
        ` ${t("common.page")} {0} ${t("common.of")} {1} ({2} ${t(
          "common.items",
        )})`,
    );

    const getClassForIcon = (recurringDuration, isAlwaysOpen) => {
      if (isAlwaysOpen && recurringDuration == 0) {
        return "las la-2x la-infinity";
      } else if (recurringDuration > 0 && !isAlwaysOpen) {
        return "las la-2x la-redo-alt";
      } else {
        return "las la-2x la-hourglass";
      }
    };

    const getToolTipData = (recurringDuration, isAlwaysOpen) => {
      if (isAlwaysOpen) {
        return t("scheme_details.always_open");
      } else if (recurringDuration > 0 && !isAlwaysOpen) {
        return t("scheme_details.recurring_deadline");
      } else {
        return "One-off application period";
      }
    };

    const formatDate = (stringDate) => {
      const date = new Date(stringDate);
      if (date.getFullYear() < 1000) return t("search_schemes.indefinite");
      else if (stringDate == null) return "-";
      return moment(date).format("DD-MM-YYYY");
    };

    const getNoDataText = () => {
      if (selectedTab.value === 0)
        return t("scheme_details.no_open_openings_or_current_deadlines");
      else if (selectedTab.value === 1)
        return t("scheme_details.there_are_no_future_openings");
      else if (selectedTab.value === 2)
        return t("scheme_details.there_are_no_past_openings");

      return t("scheme_details.no_open_openings_or_current_deadlines");
    };

    const getSortedLinksAndDocs = (links, documents) => {
      var combinedArray = [...links, ...documents];
      combinedArray.sort((a, b) =>
        a.documentTypeDescription > b.documentTypeDescription ? 1 : -1,
      );
      return combinedArray;
    };

    const getIconBasedOnExtension = (extension) => {
      switch (extension) {
        case ".pdf":
          return "las la-2x la-file-pdf";
        case ".doc":
          return "las la-2x la-file-word";
        case ".docx":
          return "las la-2x la-file-word";
        case ".xls":
          return "las la-2x la-file-excel";
        case ".xlsx":
          return "las la-2x la-file-excel";
        case ".csv":
          return "las la-2x la-file-csv";
        case ".txt":
          return "las la-2x la-file";
        default:
          return "las la-2x la-file";
      }
    };

    const download = async (documentId, documentName) => {
      await GrantsService.downloadDocument(documentId).then((response) => {
        var fileURL = window.URL.createObjectURL(
          new Blob([response.data], { type: response.data.type }),
        );
        var a = document.createElement("a");
        a.setAttribute("style", "display:none;");
        document.body.appendChild(a);
        a.download = documentName;
        a.href = fileURL;
        a.target = "_blank";
        a.click();
        document.body.removeChild(a);
      });
    };

    return {
      cardViewRef,
      schemeId,
      schemeTranslated,
      selectedTab,
      refreshCardView,
      onContentReady,
      callsDataSource,
      pageInfoText,
      openDetailsInNewTab,
      getClassForIcon,
      getToolTipData,
      formatDate,
      getNoDataText,
      getSortedLinksAndDocs,
      getIconBasedOnExtension,
      isVind,
      EDocumentType,
      isDeprecatedDocumentType,
      download,
      seeDeprecatedItems,
      updateSeeDeprecatedItems,
    };
  },
};
</script>
